import { useContext } from 'react';
import Head from 'next/head';

import { ConfigContext } from '@providers/ConfigProvider';
import { useIntl } from 'utils/intl';
import getNameByType from 'utils/getNameByType';
import resolveImageUrl from 'utils/resolveImageUrl';
import getStructuredData from 'utils/getStructuredData';
import getVideoStructuredData from 'utils/getVideoStructuredDate';

import { Props as PageProps } from '../Model.types';
import { getModelStructuredData } from './ModelHead.utils';
import messages from './ModelHead.messages';

type Props = Pick<PageProps, 'product' | 'variant' | 'variants' | 'video'>;

const ModelHead = ({ product, variant, variants, video }: Props) => {
  const intl = useIntl();
  const { appUrl, shopId, contact } = useContext(ConfigContext);

  const { id, thumbnail, urlSlug } = product;
  const { names } = variant;

  const href = `${appUrl}/model/${urlSlug}/${id}`;
  const pageTitle = getNameByType(names, 'PRODUCT_META_TITLE', shopId);
  const metaDescription = getNameByType(names, 'PRODUCT_META_DESCRIPTION', shopId);

  const thumbnailUrl = thumbnail
    ? resolveImageUrl(
        {
          preset: 'og',
          name: thumbnail.basename,
          extension: thumbnail.extension,
          productName: product.name,
        },
        { appUrl }
      )
    : undefined;

  return (
    <Head>
      <title>{pageTitle}</title>
      <meta name="robots" content="index, follow" key="robots" />
      <meta property="og:url" content={href} key="og-url" />
      <meta property="og:type" content="product.group" key="og-type" />
      <meta property="og:title" content={pageTitle} key="og-title" />
      <meta property="og:description" content={metaDescription} key="og-description" />
      {thumbnailUrl && (
        <>
          <meta property="og:image" content={thumbnailUrl} key="og-image" />
          <meta property="og:image:secure_url" content={thumbnailUrl} key="og-image-secure-url" />
          <meta property="og:image:type" content="image/png" key="og-image-type" />
          <meta
            property="og:image:alt"
            content={`${product.type.name} - ${product.name} - ${intl.formatMessage(messages.mainPhoto)}`}
            key="og-image-alt"
          />
        </>
      )}
      <meta name="description" content={metaDescription} key="description" />
      <link rel="canonical" href={href} key="canonical" />
      {getStructuredData(getModelStructuredData({ product, variant, variants }, appUrl, contact))}
      {video && getStructuredData(getVideoStructuredData(video))}
    </Head>
  );
};

export default ModelHead;
